import React, { useState, useCallback, useEffect } from 'react';
import confetti from 'canvas-confetti';
import type { TapToRevealTakeawaysScreen as TapToRevealTakeawaysScreenType } from '../../types/course';
import { playTickSound, playCorrectSound } from '../../utils/sound';

interface Props {
  screen: TapToRevealTakeawaysScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const TapToRevealTakeawaysScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  const [revealed, setRevealed] = useState<Set<number>>(
    () => completed ? new Set(screen.bullets.map((_, i) => i)) : new Set()
  );

  const allRevealed = revealed.size === screen.bullets.length;

  useEffect(() => {
    if (allRevealed && !completed) {
      onInteractionComplete();
      playCorrectSound();
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#22c55e', '#3b82f6', '#f59e0b'],
      });
    }
  }, [allRevealed, completed, onInteractionComplete]);

  const handleReveal = useCallback((index: number) => {
    if (revealed.has(index)) return;
    playTickSound();
    setRevealed((prev) => new Set(prev).add(index));
  }, [revealed]);

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      <p className="text-xs text-center text-[var(--clue-text-tertiary)]">
        Tap each card to reveal
      </p>

      <div className="space-y-2.5">
        {screen.bullets.map((bullet, i) => {
          const isRevealed = revealed.has(i);
          return (
            <button
              key={i}
              onClick={() => handleReveal(i)}
              className={`w-full text-left rounded-lg border p-4 transition-all duration-300 ${
                isRevealed
                  ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30'
                  : 'bg-[var(--clue-bg-secondary)] border-[var(--clue-border-mid)] hover:border-[var(--clue-green-accent)]/50 cursor-pointer'
              }`}
            >
              {isRevealed ? (
                <div className="flex gap-2 text-sm text-[var(--clue-text-primary)]">
                  <span className="text-[var(--clue-green-accent)] shrink-0">&#10003;</span>
                  <span>{bullet}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-[var(--clue-text-tertiary)]">
                  <span className="w-5 h-5 rounded-full bg-[var(--clue-border-mid)] flex items-center justify-center text-xs">
                    {i + 1}
                  </span>
                  <span>Tap to reveal</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
